import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { adminApi } from '../../../features/admin/admin.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { paths, routeTo } from '../../../routes/paths';

import styles from './AdminPortal.module.css';

export function AdminAuditLogsPage(): React.JSX.Element {
  const [page, setPage] = useState(1);

  const logs = useQuery({
    queryKey: ['admin', 'audit-logs', { page }],
    queryFn: () => adminApi.listAuditLogs({ page, pageSize: 25 }),
  });

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Audit log</h1>
        <p className={styles.pageSubtitle}>
          Append-only. Records cannot be edited or deleted through any interface.
        </p>
      </header>

      <section className={styles.card} aria-labelledby="audit-heading">
        <h2 className={styles.cardTitle} id="audit-heading">
          Recent activity
        </h2>

        {logs.isPending && <PageSpinner label="Loading audit records" />}
        {logs.isError && <Alert tone="error">{toDisplayMessage(logs.error)}</Alert>}

        {logs.isSuccess && logs.data.items.length === 0 && (
          <p className={styles.empty}>
            No audit records yet. Staff and system actions will appear here when activity begins.
          </p>
        )}

        {logs.isSuccess && logs.data.items.length > 0 && (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <caption>{logs.data.pagination.totalItems} records, newest first</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Action</th>
                    <th scope="col">Actor</th>
                    <th scope="col">Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.data.items.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <Link to={routeTo.auditLog(log.id)}>
                          {new Date(log.createdAt).toLocaleString()}
                        </Link>
                      </td>
                      <td>
                        <code>{log.action}</code>
                      </td>
                      <td>{log.actorEmail ?? 'System'}</td>
                      <td>{log.entityType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <nav className={styles.pagination} aria-label="Pagination">
              <Button
                variant="secondary"
                disabled={!logs.data.pagination.hasPreviousPage}
                onClick={() => {
                  setPage((current) => current - 1);
                }}
              >
                Previous
              </Button>
              <span>
                Page {logs.data.pagination.page} of {logs.data.pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={!logs.data.pagination.hasNextPage}
                onClick={() => {
                  setPage((current) => current + 1);
                }}
              >
                Next
              </Button>
            </nav>
          </>
        )}
      </section>
    </div>
  );
}

export function AdminAuditLogDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  const log = useQuery({
    queryKey: ['admin', 'audit-logs', id],
    queryFn: () => adminApi.getAuditLog(id),
  });

  if (log.isPending) {
    return <PageSpinner label="Loading the record" />;
  }

  if (log.isError) {
    return <Alert tone="error">{toDisplayMessage(log.error)}</Alert>;
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{log.data.action}</h1>
        <Link to={paths.adminAuditLogs}>Back to the audit log</Link>
      </header>

      <section className={styles.card} aria-labelledby="record-heading">
        <h2 className={styles.cardTitle} id="record-heading">
          Record
        </h2>

        <dl className={styles.definitionList}>
          <dt>When</dt>
          <dd>{new Date(log.data.createdAt).toLocaleString()}</dd>

          <dt>Actor</dt>
          <dd>{log.data.actorEmail ?? 'System'}</dd>

          <dt>Entity</dt>
          <dd>
            {log.data.entityType}
            {log.data.entityId === null ? '' : ` · ${log.data.entityId}`}
          </dd>

          <dt>Reason</dt>
          <dd>{log.data.reason ?? '—'}</dd>

          <dt>Address</dt>
          <dd>{log.data.ipAddress ?? '—'}</dd>

          <dt>Request id</dt>
          <dd>{log.data.correlationId ?? '—'}</dd>
        </dl>
      </section>

      <section className={styles.card} aria-labelledby="change-heading">
        <h2 className={styles.cardTitle} id="change-heading">
          Recorded change
        </h2>
        <p className={styles.pageSubtitle}>
          Sensitive fields are replaced with a redaction marker before the record is written, so
          they were never stored in the first place.
        </p>

        <h3>Before</h3>
        <pre className={styles.pre}>{formatJson(log.data.before)}</pre>

        <h3>After</h3>
        <pre className={styles.pre}>{formatJson(log.data.after)}</pre>
      </section>
    </div>
  );
}

function formatJson(value: unknown): string {
  return value === null || value === undefined ? '—' : JSON.stringify(value, null, 2);
}
